
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Speaker, MicOff, Volume2, Volume, VolumeX } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { useToast } from '@/hooks/use-toast';

interface DirectComputerAudioStreamingProps {
  isActive?: boolean;
  onToggle?: (isActive: boolean) => void;
}

const DirectComputerAudioStreaming: React.FC<DirectComputerAudioStreamingProps> = ({
  isActive = false,
  onToggle
}) => {
  const [isStreaming, setIsStreaming] = useState(isActive);
  const [volume, setVolume] = useState(75);
  const { toast } = useToast();

  const handleToggleStreaming = () => {
    const newState = !isStreaming;
    setIsStreaming(newState);
    
    if (onToggle) {
      onToggle(newState);
    }
    
    if (newState) {
      toast({
        title: "System Audio Sharing Enabled",
        description: "Other participants can now hear your computer's audio",
        duration: 3000,
      });
    } else {
      toast({
        title: "System Audio Sharing Disabled",
        description: "Your computer's audio is no longer being shared",
        duration: 3000,
      });
    }
  };

  return (
    <div className="p-4 rounded-lg bg-black/40 backdrop-blur-sm border border-white/10 animate-fade-in">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Speaker className={`h-5 w-5 ${isStreaming ? 'text-green-400' : 'text-muted-foreground'}`} />
          <h3 className="font-medium">System Audio Sharing</h3>
        </div>
        <Switch 
          checked={isStreaming}
          onCheckedChange={handleToggleStreaming}
        />
      </div>
      
      {isStreaming && (
        <div className="space-y-4 py-2">
          <div className="flex items-center gap-2">
            <VolumeX className="h-4 w-4 text-muted-foreground" />
            <Slider 
              value={[volume]}
              max={100}
              step={1}
              onValueChange={(values) => setVolume(values[0])}
              className="flex-1"
            />
            <Volume2 className="h-4 w-4 text-muted-foreground" />
          </div>
          
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>System Volume: {volume}%</span>
            <span>Sharing Active</span>
          </div>
        </div>
      )}
      
      <p className="text-xs text-muted-foreground mt-4">
        {isStreaming 
          ? "Other participants can now hear any audio playing on your device"
          : "Share your computer's audio like music or videos with other participants"}
      </p>
    </div>
  );
};

export default DirectComputerAudioStreaming;
